// src/composables/useMemberRestrictions.js
//
// Reflete no lado do MEMBRO (conta gerenciada) o mesmo enforcement que o backend
// aplica, para esconder/desabilitar ações que resultariam em 403.
//
// Regras (quando a opção "Somente conteúdo do titular" está ligada — restrictToTitular):
//   • Textos/Atividades: sem criar/editar/excluir.
//   • Notas: criar só em atividade criada pelo titular; editar/excluir só as
//     próprias notas em atividades criadas pelo titular.
// Quando a opção está desligada, valem apenas as permissões do grid do vínculo.
// Para quem NÃO é membro gerenciado (titular/comum), nada é restringido.

import { computed, watch } from "vue";
import { useUserStore } from "@/stores/useUserStore";
import { useManagedAccountStore } from "@/stores/useManagedAccountStore";
import { useActivityStore } from "@/stores/useActivityStore";
import { decodeManagerUserId } from "@/utils/permissionDefaults";

export function useMemberRestrictions() {
  const userStore = useUserStore();
  const managedAccountStore = useManagedAccountStore();

  const isManagedMember = computed(
    () => userStore.currentUser?.loginContext === "managed"
  );

  const perms = computed(() => managedAccountStore.currentMemberPermissions);

  // O círculo do contexto é necessário para decidir em quais atividades o membro
  // pode anotar; carrega uma vez, só quando ele é membro gerenciado.
  watch(
    isManagedMember,
    (value) => {
      if (value) managedAccountStore.ensureCircle();
    },
    { immediate: true }
  );

  // Só restringe membro gerenciado; para os demais, sem restrição.
  const restrictToTitular = computed(
    () => isManagedMember.value && perms.value?.restrictToTitular === true
  );

  const managerUserId = computed(() =>
    isManagedMember.value ? decodeManagerUserId() : null
  );

  const currentUserId = computed(() => userStore.currentUser?.userId ?? null);

  // grid do vínculo: null/ausente = sem restrição (titular/comum).
  function gridAllows(resource, action) {
    const p = perms.value;
    if (!p) return true;
    return p[resource]?.[action] ?? false;
  }

  // ── Textos ────────────────────────────────────────────────────────────────
  const canCreateText = computed(
    () => gridAllows("texts", "create") && !restrictToTitular.value
  );
  const canEditText = computed(
    () => gridAllows("texts", "edit") && !restrictToTitular.value
  );
  const canDeleteText = computed(
    () => gridAllows("texts", "delete") && !restrictToTitular.value
  );

  // ── Atividades ──────────────────────────────────────────────────────────────
  const canCreateActivity = computed(
    () => gridAllows("activities", "create") && !restrictToTitular.value
  );
  const canEditActivity = computed(
    () => gridAllows("activities", "edit") && !restrictToTitular.value
  );
  const canDeleteActivity = computed(
    () => gridAllows("activities", "delete") && !restrictToTitular.value
  );

  // ── Notas ─────────────────────────────────────────────────────────────────
  // Criar: grid.notes.create E (!restrict OU atividade-pai criada pelo titular).
  // Trabalhar numa atividade (criar, editar ou excluir nota) exige "selecionar"
  // sobre ela — espelha EnsureCanWorkInActivityAsync no backend. As permissões
  // efetivas já vêm calculadas do servidor em selectedActivity.userPermissions,
  // com a precedência usuário > grupo aplicada.
  //
  // Só decide quando a atividade aberta é a mesma da pergunta: em telas gerais
  // (notas de várias atividades) a seleção do leitor não vale, e aí o servidor
  // continua sendo a barreira.
  function canWorkInActivity(activityCreatedByUserId) {
    const activity = useActivityStore().selectedActivity;
    if (!activity || activity.createdByUserId == null) return true;
    if (Number(activity.createdByUserId) !== Number(activityCreatedByUserId))
      return true;

    const permissions = activity.userPermissions;
    if (!Array.isArray(permissions) || permissions.length === 0) return true;

    return permissions.some((perm) =>
      ["selecionar", "editar", "excluir", "deletar"].includes(
        (perm.permissionName || "").toLowerCase()
      )
    );
  }

  // "Somente conteúdo do titular" vale para o CÍRCULO do titular do contexto —
  // quem prepara as atividades de uma escola são os professores, não a conta da
  // escola. Espelha EnsureMemberCanWriteNoteAsync no backend.
  function isCircleActivity(activityCreatedByUserId) {
    if (activityCreatedByUserId == null) return false;

    const authorId = Number(activityCreatedByUserId);
    if (authorId === managerUserId.value) return true;

    const circle = managedAccountStore.circleUserIds || [];
    return circle.map((id) => Number(id)).includes(authorId);
  }

  function canCreateNoteInActivity(activityCreatedByUserId) {
    if (!gridAllows("notes", "create")) return false;
    if (!canWorkInActivity(activityCreatedByUserId)) return false;
    if (!restrictToTitular.value) return true;
    return isCircleActivity(activityCreatedByUserId);
  }

  // Nível-atividade (ex.: ações em lote / mostrar menu): grid.notes.<action> E
  // ACL da atividade E (!restrict OU atividade do círculo). A autoria por nota é
  // validada por nota (canModifyNote) e, em última instância, pelo backend.
  function canManageNotesInActivity(action, activityCreatedByUserId) {
    if (!gridAllows("notes", action)) return false;
    if (!canWorkInActivity(activityCreatedByUserId)) return false;
    if (!restrictToTitular.value) return true;
    return isCircleActivity(activityCreatedByUserId);
  }

  // Editar/excluir uma nota específica: grid.notes.<action> E
  // (!restrict OU (nota própria E atividade do titular)).
  function canModifyNote(action, noteCreatedByUserId, activityCreatedByUserId) {
    if (!gridAllows("notes", action)) return false;
    if (!canWorkInActivity(activityCreatedByUserId)) return false;
    if (!restrictToTitular.value) return true;
    return (
      noteCreatedByUserId != null &&
      Number(noteCreatedByUserId) === currentUserId.value &&
      isCircleActivity(activityCreatedByUserId)
    );
  }

  // ── Delegação administrativa ──────────────────────────────────────────────
  // Membro com can_manage_circle no vínculo cadastra e vincula contas em nome do
  // titular do contexto (professor/administrador montando turma). Quem não é
  // membro gerenciado administra o próprio círculo normalmente.
  const canManageCircle = computed(() => {
    if (!isManagedMember.value) return true;
    return perms.value?.canManageCircle === true;
  });

  return {
    isManagedMember,
    canManageCircle,
    restrictToTitular,
    managerUserId,
    currentUserId,
    canCreateText,
    canEditText,
    canDeleteText,
    canCreateActivity,
    canEditActivity,
    canDeleteActivity,
    canCreateNoteInActivity,
    canManageNotesInActivity,
    canModifyNote,
  };
}
